import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RadioGroup,
  RichTextEditor,
  SectionCard,
  SegmentedControl,
  StatusBadge,
  StickyFooter,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import { UserSearchSelect } from '@/features/users/components/UserSearchSelect';
import { NOTIFICATION_INTEGRATION_WRITE_ROLES } from '../lib/notificationActionRbac';
import { jobStatusLabel, messageStatusTone } from '../lib/notificationLabels';
import { useBulkDispatchMutation, type BulkDispatchInput, type BulkRecipientInput } from '../hooks/useBulkDispatchMutation';
import { useBulkJobQuery } from '../hooks/useBulkJobQuery';
import { useEmailImageUpload } from '../hooks/useEmailImageUpload';

type InfoChannel = 'SMS' | 'EMAIL';
type InfoTarget = 'member' | 'manual';

const denseInputStyle = {
  background: 'var(--surface-0)',
  border: '1px solid var(--divider-strong)',
  color: 'var(--content-default)',
} as const;

/** 에디터 HTML 이 비었는가 — 글자도 이미지도 없으면 빈 본문(<p><br></p> 등). */
export const isEmptyEmailHtml = (html: string): boolean =>
  !/<img\b/i.test(html) && html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '').length === 0;

/** BE rawBody 상한(notification 전달 문서 — 메일 HTML 은 이미지 태그·서식 때문에 길다). */
export const EMAIL_HTML_MAX = 100_000;

export const parseInfoRecipients = (text: string): string[] =>
  [...new Set(text.split(/[\r\n,;]+/u).map((l) => l.trim()).filter((l) => l.length > 0))];

/**
 * 정보성 메시지 탭 (QA-419) — 계좌 안내·CS 안내·상품 안내처럼 회원 개인에게 보내는 운영 메시지를 문자·메일로 직접 작성해 보낸다.
 *
 * - 알림톡은 제외(승인 템플릿만 보낼 수 있다). 템플릿 없이 본문을 직접 쓴다(BE rawBody/rawSubject).
 * - 광고가 아니라서 (광고) 표기·야간 보류·수신거부 안내가 붙지 않는다 — BE 는 광고성 템플릿일 때만 적용한다.
 * - 수신자는 회원 선택·직접 입력만(전체·등급 대량 발송은 광고성 탭). `informational: true` 로 보내 BE 가
 *   마케팅 수신 동의와 무관하게 보내게 한다(BE 전달 문서 — 반영 전에는 미동의 회원이 건너뛰어진다).
 */
export const InformationalTab = () => {
  const { t } = useTranslation('notifications');
  const canWrite = useHasRole(NOTIFICATION_INTEGRATION_WRITE_ROLES);
  const dispatch = useBulkDispatchMutation();
  const uploadEmailImage = useEmailImageUpload();

  const [channel, setChannel] = useState<InfoChannel>('SMS');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // QA-419 — 메일 본문은 에디터(HTML, 이미지 포함). 문자 본문과 따로 둔다(채널을 바꿔도 서로 덮지 않게).
  const [emailHtml, setEmailHtml] = useState('');
  const [target, setTarget] = useState<InfoTarget>('member');
  const [memberUserIds, setMemberUserIds] = useState<ReadonlyArray<string>>([]);
  const [manualText, setManualText] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);
  const [trackedJobId, setTrackedJobId] = useState<string | null>(null);
  const jobQuery = useBulkJobQuery(trackedJobId);

  const recipients: BulkRecipientInput[] =
    target === 'member'
      ? memberUserIds.map((userId) => ({ userId }))
      : parseInfoRecipients(manualText).map((external) => ({ external }));

  const hasContent =
    channel === 'SMS'
      ? body.trim().length > 0
      : subject.trim().length > 0 && !isEmptyEmailHtml(emailHtml) && emailHtml.length <= EMAIL_HTML_MAX;
  const canSend = canWrite && hasContent && recipients.length > 0 && !dispatch.isPending;

  const onClickSend = () => {
    if (recipients.length === 0) {
      toast.error(t('informational.toast.noRecipients', '수신자를 한 명 이상 지정하세요.'));
      return;
    }
    if (!hasContent) {
      toast.error(
        channel === 'EMAIL'
          ? t('informational.toast.noEmailContent', '메일 제목과 본문을 입력하세요.')
          : t('informational.toast.noSmsContent', '문자 본문을 입력하세요.'),
      );
      return;
    }
    setConfirmSend(true);
  };

  const doSend = () => {
    const payload: BulkDispatchInput =
      channel === 'EMAIL'
        ? { channel, rawSubject: subject.trim(), rawBody: emailHtml, rawHtml: true, recipients, informational: true }
        : { channel, rawBody: body, recipients, informational: true };
    dispatch.mutate(payload, {
      onSuccess: (job) => {
        setTrackedJobId(job.jobId);
        setConfirmSend(false);
        toast.success(t('informational.toast.sent', '발송을 접수했습니다. 결과는 아래 진행률과 발송 내역에서 확인하세요.'));
      },
      onError: (err) => {
        setConfirmSend(false);
        toast.error(friendlyMessage(err, t('informational.toast.fail', '발송하지 못했습니다.')));
      },
    });
  };

  const channelLabel = channel === 'SMS' ? t('channel.SMS', 'SMS') : t('channel.EMAIL', '이메일');

  return (
    <div className="flex flex-col gap-4" data-testid="informational-tab">
      <p
        className="rounded-md p-3 text-[13px]"
        style={{ background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' }}
        role="note"
        data-testid="informational-notice"
      >
        {t(
          'informational.notice',
          '계좌 안내·CS 안내·상품 안내처럼 회원에게 꼭 전해야 하는 정보성 메시지를 문자·메일로 바로 보냅니다. (광고) 표기·야간 발송 제한이 붙지 않으니, 광고·홍보 내용은 [광고성 메시지]에서 보내세요.',
        )}
      </p>

      <SectionCard title={t('informational.section.message', '메시지 내용')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('promotional.field.channel', '발송 채널')}
            </span>
            <SegmentedControl<InfoChannel>
              value={channel}
              onChange={setChannel}
              options={[
                { value: 'SMS', label: t('channel.SMS', 'SMS') },
                { value: 'EMAIL', label: t('channel.EMAIL', '이메일') },
              ]}
              aria-label={t('promotional.field.channel', '발송 채널')}
            />
          </div>

          {channel === 'EMAIL' ? (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="informational-subject" className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('informational.field.subject', '메일 제목')}
              </label>
              <Input
                id="informational-subject"
                value={subject}
                maxLength={200}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('informational.field.subjectPlaceholder', '예: [에버라인] 환불 계좌 안내')}
              />
            </div>
          ) : null}

          {channel === 'EMAIL' ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('informational.field.emailBody', '메일 본문')}
              </span>
              <RichTextEditor
                value={emailHtml}
                onChange={setEmailHtml}
                minHeight={320}
                aria-label={t('informational.field.emailBody', '메일 본문')}
                onUploadImage={uploadEmailImage}
                imageGuide={t('informational.field.emailImageGuide', { defaultValue: '이미지는 가로 600px 안팎이 메일에서 보기 좋습니다(주문 내역 캡처·안내 흐름도 등).' })}
                onUploadError={(e) => toast.error(friendlyMessage(e, t('informational.toast.imageFail', '이미지를 올리지 못했습니다.')))}
              />
              {emailHtml.length > EMAIL_HTML_MAX ? (
                <span className="text-[11px]" style={{ color: 'var(--signal-danger)' }} role="alert">
                  {t('informational.field.emailTooLong', '본문이 너무 깁니다. 서식이나 내용을 줄여 주세요.')}
                </span>
              ) : null}
            </div>
          ) : (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="informational-body" className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('informational.field.smsBody', '문자 본문')}
            </label>
            <textarea
              id="informational-body"
              rows={5}
              maxLength={4000}
              className="w-full rounded-md px-2.5 py-1.5 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
              style={denseInputStyle}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('informational.field.bodyPlaceholder', '보낼 내용을 입력하세요.')}
            />
            <span className="self-end text-[11px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
              {body.length.toLocaleString()} / 4,000
            </span>
          </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title={t('informational.section.recipients', '수신자')}>
        <div className="flex flex-col gap-4">
          <RadioGroup
            value={target}
            onValueChange={(v) => setTarget(v as InfoTarget)}
            orientation="horizontal"
            options={[
              { value: 'member', label: t('informational.recipients.member', '회원 선택') },
              { value: 'manual', label: t('informational.recipients.manual', '직접 입력') },
            ]}
            aria-label={t('informational.section.recipients', '수신자')}
          />
          {target === 'member' ? (
            <UserSearchSelect
              multi
              value={memberUserIds}
              onChange={setMemberUserIds}
              testId="informational-members"
              ariaLabel={t('informational.recipients.member', '회원 선택')}
              emptyHint={t('informational.recipients.memberHint', '회원에게 저장된 휴대폰 번호·메일 주소로 보냅니다.')}
            />
          ) : (
            <textarea
              rows={4}
              className="w-full rounded-md px-2.5 py-1.5 font-mono text-[12px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
              style={denseInputStyle}
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              placeholder={
                channel === 'EMAIL'
                  ? t('informational.recipients.manualEmail', '메일 주소를 줄바꿈 또는 쉼표로 구분해 입력하세요.')
                  : t('informational.recipients.manualSms', '휴대폰 번호를 줄바꿈 또는 쉼표로 구분해 입력하세요.')
              }
              aria-label={t('informational.recipients.manual', '직접 입력')}
              data-testid="informational-manual"
            />
          )}
          <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }} data-testid="informational-count">
            {t('informational.recipients.count', '수신자 {{count}}명', { count: recipients.length })}
          </span>
        </div>
      </SectionCard>

      {trackedJobId && jobQuery.data ? (
        <SectionCard title={t('promotional.section.progress', '발송 진행률')}>
          <dl className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-3">
            <div>
              <dt style={{ color: 'var(--content-muted)' }}>{t('promotional.progress.status', '상태')}</dt>
              <dd className="mt-1">
                <StatusBadge label={jobStatusLabel(t, jobQuery.data.status)} variant={messageStatusTone(jobQuery.data.status)} />
              </dd>
            </div>
            <div>
              <dt style={{ color: 'var(--content-muted)' }}>{t('promotional.progress.success', '성공')}</dt>
              <dd className="mt-1 tabular-nums" style={{ color: 'var(--content-strong)' }}>
                {jobQuery.data.successCount} / {jobQuery.data.totalCount}
              </dd>
            </div>
            <div>
              <dt style={{ color: 'var(--content-muted)' }}>{t('promotional.progress.fail', '실패')}</dt>
              <dd className="mt-1 tabular-nums" style={{ color: 'var(--signal-danger)' }}>
                {jobQuery.data.failCount}
              </dd>
            </div>
          </dl>
        </SectionCard>
      ) : null}

      <StickyFooter>
        <Button size="sm" disabled={!canSend} onClick={onClickSend} data-testid="informational-send">
          {dispatch.isPending ? t('promotional.actions.sending', '발송 중…') : t('promotional.actions.send', '발송')}
        </Button>
      </StickyFooter>

      <Dialog open={confirmSend} onOpenChange={setConfirmSend}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('informational.confirm.title', '정보성 메시지 발송')}</DialogTitle>
            <DialogDescription>
              {t('informational.confirm.body', '{{channel}}로 {{count}}명에게 바로 보냅니다. 보낸 뒤에는 취소할 수 없습니다.', {
                channel: channelLabel,
                count: recipients.length,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('common.cancel', '취소')}
              </Button>
            </DialogClose>
            <Button size="sm" disabled={dispatch.isPending} onClick={doSend} data-testid="informational-confirm">
              {dispatch.isPending ? t('promotional.actions.sending', '발송 중…') : t('promotional.actions.send', '발송')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
